import React, { useState } from 'react';
import { Box, Typography, Alert, Grid, Button, useTheme, useMediaQuery } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { SidebarLayout } from '@/components/Layout';
import ConversationsList from '@/components/Chat/ConversationsList';
import BookingMessages from '@/components/Chat/BookingMessages';
import { useAuth } from '@/contexts/AuthContext';

interface Conversation {
  bookingId: string;
  listingTitle: string;
  checkInDate: string;
  checkOutDate: string;
  status: string;
  otherParticipant: {
    email: string;
    name: string;
    type: 'host' | 'guest';
  };
  lastMessage?: {
    content: string;
    createdAt: string;
    senderEmail: string;
    senderType: 'host' | 'guest';
  };
  unreadCount: number;
  lastMessageAt: string;
  createdAt: string;
}

const Chat: React.FC = () => {
  const { userProfile: user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);

  if (!user) {
    return (
      <SidebarLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Please log in to access the chat system.
          </Alert>
        </Box>
      </SidebarLayout>
    );
  }

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
  };

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
          Messages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Communicate with your {user.primaryRole === 'host' ? 'guests' : 'hosts'}
        </Typography>
      </Box>
      
      {isMobile ? (
        selectedConversation ? (
          <Box sx={{ height: 'calc(100vh - 200px)' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setSelectedConversation(null)}
              sx={{ mb: 1 }}
            >
              Back to conversations
            </Button>
            <BookingMessages
              bookingId={selectedConversation.bookingId}
              otherParticipant={selectedConversation.otherParticipant}
              listingTitle={selectedConversation.listingTitle}
            />
          </Box>
        ) : (
          <Box sx={{ height: 'calc(100vh - 200px)' }}>
            <ConversationsList
              onConversationSelect={handleConversationSelect}
              selectedBookingId={undefined}
            />
          </Box>
        )
      ) : (
        <Grid container spacing={2} sx={{ height: '600px' }}>
          <Grid item xs={12} md={4}>
            <ConversationsList
              onConversationSelect={handleConversationSelect}
              selectedBookingId={selectedConversation?.bookingId}
            />
          </Grid>
          
          <Grid item xs={12} md={8}>
            {selectedConversation ? (
              <BookingMessages
                bookingId={selectedConversation.bookingId}
                otherParticipant={selectedConversation.otherParticipant}
                listingTitle={selectedConversation.listingTitle}
              />
            ) : (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                height="100%"
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  bgcolor: 'background.paper'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a conversation to start messaging
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </SidebarLayout>
  );
};

export default Chat;